// ── AR Flow — NOVA INTERFACE: 📊 Marketing Intelligence (só administração) ──
// Mesma fonte do Marketing Intelligence que já existe no CRM:
//   GET  /api/crm/marketing-intelligence      (evidência do 1º contato até a venda)
//   POST /api/crm/marketing-intelligence/ai   (pergunta em linguagem natural)
// O servidor só libera para quem tem a capacidade "marketing_intelligence".
//
// Mídia paga (investimento da Meta): quando a coleta diária estiver ligada, o
// servidor passa a mandar `report.midia` (ver LEIA-ME). Até lá, as colunas de
// investimento mostram "aguarda Meta" — nunca zero, nunca número inventado.

import { useCallback, useEffect, useMemo, useState } from "react";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";
const brl = v => (v == null || v === "" || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }));
const num = v => (v == null ? "—" : Number(v).toLocaleString("pt-BR"));
const pct = (a, b) => (b ? `${((a / b) * 100).toFixed(1).replace(".", ",")}%` : "—");
const dia = d => d.toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const digitos = v => String(v || "").replace(/\D/g, "");

export function periodo(preset, hoje = new Date()) {
  const h = dia(hoje);
  const d = new Date(`${h}T12:00:00`);
  if (preset === "hoje") return { start: h, end: h };
  if (preset === "7d") { d.setDate(d.getDate() - 6); return { start: dia(d), end: h }; }
  if (preset === "mes_passado") {
    const ini = new Date(d.getFullYear(), d.getMonth() - 1, 1, 12), fim = new Date(d.getFullYear(), d.getMonth(), 0, 12);
    return { start: dia(ini), end: dia(fim) };
  }
  return { start: `${h.slice(0, 7)}-01`, end: h }; // mês atual
}

// Agrupa as evidências por campanha (contatos únicos por etapa) e junta a mídia, se houver.
export function agruparCampanhas(report) {
  const rec = report?.records || {};
  const chave = r => `${r.storeId || ""}:${digitos(r.phone) || r.id}`;
  const mapa = new Map();
  const pegar = r => {
    const nome = r.campaign || "Sem campanha identificada";
    if (!mapa.has(nome)) mapa.set(nome, { campanha: nome, campaignId: r.campaignId || null, leads: new Set(), atendidos: new Set(), qualificados: new Set(), visitas: new Set(), vendas: new Set(), faturamento: 0 });
    return mapa.get(nome);
  };
  (rec.leadEntries || []).forEach(r => pegar(r).leads.add(chave(r)));
  [...(rec.firstResponses || []), ...(rec.effectiveHumanServices || [])].forEach(r => pegar(r).atendidos.add(chave(r)));
  (rec.qualified || []).forEach(r => pegar(r).qualificados.add(chave(r)));
  (rec.visits || []).forEach(r => pegar(r).visitas.add(chave(r)));
  (rec.sales || []).forEach(r => { const g = pegar(r); g.vendas.add(chave(r)); g.faturamento += Number(r.value) || 0; });
  const midia = report?.midia?.porCampanha || {};
  return [...mapa.values()].map(g => {
    const m = midia[g.campaignId] || midia[g.campanha] || null;
    const investido = m && m.investido != null ? Number(m.investido) : null;
    const leads = g.leads.size, vendas = g.vendas.size;
    return {
      campanha: g.campanha, leads, atendidos: g.atendidos.size, qualificados: g.qualificados.size, visitas: g.visitas.size, vendas,
      faturamento: g.faturamento, investido,
      custoLead: investido != null && leads ? investido / leads : null,
      custoVenda: investido != null && vendas ? investido / vendas : null,
      retorno: investido ? g.faturamento / investido : null,
    };
  }).sort((a, b) => b.faturamento - a.faturamento || b.leads - a.leads);
}

// Enquanto os números não forem conferidos com a Ana (diagnóstico da origem dos
// leads + Meta), a tela NÃO mostra dados: só o aviso de construção. Para ligar,
// troque para true depois da validação.
export const MARKETING_DADOS_VALIDADOS = false;

export default function MarketingNova(props) {
  if (!MARKETING_DADOS_VALIDADOS) {
    const th = props.theme;
    return (
      <div>
        <div style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 700, color: th.text }}>Marketing Intelligence</div>
        <div style={{ fontSize: 14, color: th.muted, marginBottom: 14 }}>Qual campanha traz venda, e não só lead.</div>
        <div role="status" style={{ border: `1px dashed ${th.nuLine || th.sandDark}`, background: th.sandDark, borderRadius: 4, padding: "18px 20px", color: th.text }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Em construção</div>
          <div style={{ fontSize: 14, color: th.muted, marginTop: 4, lineHeight: 1.5 }}>Estamos conferindo a origem de cada lead e conectando as contas de anúncio da Meta. Os números aparecem aqui só depois de validados.</div>
        </div>
      </div>
    );
  }
  return <MarketingPainel {...props} />;
}

function MarketingPainel({ theme, stores }) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const [preset, setPreset] = useState("mes");
  const [storeId, setStoreId] = useState("");
  const [report, setReport] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [pergunta, setPergunta] = useState("");
  const [resposta, setResposta] = useState("");
  const [perguntando, setPerguntando] = useState(false);
  const per = useMemo(() => periodo(preset), [preset]);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro("");
    try {
      const q = new URLSearchParams({ start: per.start, end: per.end, ...(storeId ? { storeId } : {}) });
      const r = await fetch(`/api/crm/marketing-intelligence?${q}`, { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(r.status === 403 ? "Esta área é só da administração geral." : (d.error || "Não foi possível carregar o relatório."));
      setReport(d);
    } catch (e) { setErro(e.message); setReport(null); } finally { setCarregando(false); }
  }, [per, storeId]);
  useEffect(() => { carregar(); }, [carregar]);

  const perguntar = async () => {
    if (!pergunta.trim() || perguntando) return;
    setPerguntando(true); setResposta("");
    try {
      const r = await fetch("/api/crm/marketing-intelligence/ai", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start: per.start, end: per.end, storeId, question: pergunta }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Não foi possível consultar agora.");
      setResposta(d.answer || d.response || "Sem resposta disponível.");
    } catch (e) { setResposta(e.message); } finally { setPerguntando(false); }
  };

  const s = report?.summary || {};
  const campanhas = useMemo(() => agruparCampanhas(report), [report]);
  const temMidia = Boolean(report?.midia?.conectado);
  const investidoTotal = temMidia ? campanhas.reduce((t, c) => t + (c.investido || 0), 0) : null;
  const contasMidia = report?.midia?.contas || [];
  const statusConta = id => contasMidia.find(c => Number(c.storeId) === Number(id))?.status;

  const botao = ativo => ({ border: `1px solid ${ativo ? th.accent : line}`, background: ativo ? th.accent : "#fff", color: ativo ? "#fff" : th.text, borderRadius: 99, padding: "6px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" });
  const cartao = { background: "#fff", border: `1px solid ${line}`, borderRadius: 4, padding: "12px 14px" };
  const numero = (rotulo, valor, detalhe) => (
    <div style={cartao}>
      <div style={{ fontSize: 12, fontWeight: 700, color: th.muted }}>{rotulo}</div>
      <div style={{ fontFamily: MONO, fontSize: 22, color: th.text, marginTop: 4 }}>{valor}</div>
      {detalhe && <div style={{ fontSize: 12, color: th.muted, marginTop: 2 }}>{detalhe}</div>}
    </div>
  );
  const funil = [["Leads", s.leadEntries], ["Atendidos", s.answeredConversations ?? s.effectiveHumanServices], ["Qualificados", s.qualified], ["Foram à loja", s.visits], ["Vendas", s.salesCount]];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 700, color: th.text }}>Marketing Intelligence</div>
          <div style={{ fontSize: 14, color: th.muted }}>Qual campanha traz venda, e não só lead.</div>
        </div>
        <select aria-label="Loja" value={storeId} onChange={e => setStoreId(e.target.value)} style={{ padding: "7px 10px", borderRadius: 4, border: `1px solid ${line}`, fontFamily: "inherit", fontSize: 14 }}>
          <option value="">Todas as lojas</option>{stores.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "12px 0 16px" }}>
        {[["hoje", "Hoje"], ["7d", "7 dias"], ["mes", "Mês atual"], ["mes_passado", "Mês passado"]].map(([id, n]) => (
          <button key={id} type="button" aria-pressed={preset === id} onClick={() => setPreset(id)} style={botao(preset === id)}>{n}</button>
        ))}
      </div>

      {erro && <div role="alert" style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", borderRadius: 4, padding: "9px 12px", fontSize: 14, marginBottom: 14 }}>{erro} <button type="button" onClick={carregar} style={{ marginLeft: 8, fontFamily: "inherit" }}>Tentar de novo</button></div>}
      {carregando && <div style={{ color: th.muted, fontSize: 14 }}>Carregando…</div>}

      {!carregando && report && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            {numero("Investido (Meta)", temMidia ? brl(investidoTotal) : "aguarda Meta", temMidia ? null : "conexão em preparação")}
            {numero("Leads", num(s.leadEntries), temMidia && s.leadEntries ? `custo ${brl(investidoTotal / s.leadEntries)} por lead` : null)}
            {numero("Vendas", num(s.salesCount), temMidia && s.salesCount ? `custo ${brl(investidoTotal / s.salesCount)} por venda` : null)}
            {numero("Faturamento", brl(s.revenue), s.ticketMedio ? `ticket ${brl(s.ticketMedio)}` : null)}
            {numero("Retorno", temMidia && investidoTotal ? `${(Number(s.revenue || 0) / investidoTotal).toFixed(1).replace(".", ",")}x` : "aguarda Meta", "faturamento ÷ investido")}
          </div>

          <div style={{ ...cartao, marginTop: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: th.text, marginBottom: 10 }}>Do lead à venda</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8 }}>
              {funil.map(([n, v], i) => (
                <div key={n} style={{ borderLeft: `3px solid ${i === funil.length - 1 ? th.accent : line}`, paddingLeft: 10 }}>
                  <div style={{ fontSize: 12, color: th.muted }}>{n}</div>
                  <div style={{ fontFamily: MONO, fontSize: 20, color: th.text }}>{num(v)}</div>
                  <div style={{ fontSize: 11, color: th.muted }}>{i ? `${pct(Number(v) || 0, Number(funil[i - 1][1]) || 0)} da etapa anterior` : "base do período"}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...cartao, marginTop: 12, overflowX: "auto" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: th.text }}>Campanhas</div>
            <div style={{ fontSize: 12, color: th.muted, margin: "2px 0 10px" }}>Contatos únicos por etapa. Investimento e custos aparecem quando a Meta estiver conectada.</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 860 }}>
              <thead><tr>{["Campanha", "Investido", "Leads", "Custo/lead", "Qualificados", "Foram à loja", "Vendas", "Custo/venda", "Faturamento", "Retorno"].map(c => (
                <th key={c} style={{ textAlign: c === "Campanha" ? "left" : "right", padding: "7px 6px", borderBottom: `2px solid ${line}`, color: th.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{c}</th>
              ))}</tr></thead>
              <tbody>
                {campanhas.map(c => (
                  <tr key={c.campanha} style={{ borderBottom: `1px solid ${line}` }}>
                    <td style={{ padding: "8px 6px", fontWeight: 700, color: th.text }}>{c.campanha}</td>
                    {[c.investido == null ? "aguarda Meta" : brl(c.investido), num(c.leads), c.custoLead == null ? "—" : brl(c.custoLead), num(c.qualificados), num(c.visitas), num(c.vendas),
                      c.custoVenda == null ? "—" : brl(c.custoVenda), brl(c.faturamento), c.retorno == null ? "—" : `${c.retorno.toFixed(1).replace(".", ",")}x`].map((v, i) => (
                      <td key={i} style={{ padding: "8px 6px", textAlign: "right", fontFamily: MONO, color: v === "aguarda Meta" ? th.muted : th.text, whiteSpace: "nowrap" }}>{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {!campanhas.length && <div style={{ padding: 16, color: th.muted, fontSize: 14 }}>Nenhum lead neste período.</div>}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginTop: 12 }}>
            <div style={cartao}>
              <div style={{ fontWeight: 700, fontSize: 15, color: th.text, marginBottom: 8 }}>Contas de anúncio</div>
              {stores.map(l => {
                const st = statusConta(l.id);
                return (
                  <div key={l.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: `1px solid ${line}`, fontSize: 14 }}>
                    <span style={{ color: th.text }}>{l.name}</span>
                    <span style={{ color: st === "conectada" ? th.positive : th.muted, fontWeight: 700 }}>{st === "conectada" ? "conectada" : st === "erro" ? "com erro" : "não conectada"}</span>
                  </div>
                );
              })}
            </div>
            <div style={cartao}>
              <div style={{ fontWeight: 700, fontSize: 15, color: th.text }}>Pergunte</div>
              <div style={{ fontSize: 12, color: th.muted, margin: "2px 0 8px" }}>Responde só sobre o período e a loja escolhidos.</div>
              <textarea value={pergunta} onChange={e => setPergunta(e.target.value)} rows={3} placeholder="Ex.: qual campanha trouxe mais gente que foi à loja?"
                style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${line}`, borderRadius: 4, padding: 9, fontFamily: "inherit", fontSize: 14, resize: "vertical" }} />
              <button type="button" onClick={perguntar} disabled={perguntando || !pergunta.trim()} style={{ marginTop: 8, border: "none", borderRadius: 4, padding: "9px 14px", background: th.accent, color: "#fff", fontWeight: 700, cursor: "pointer", opacity: perguntando || !pergunta.trim() ? 0.5 : 1, fontFamily: "inherit" }}>
                {perguntando ? "Analisando…" : "Perguntar"}
              </button>
              {resposta && <div style={{ marginTop: 10, background: th.sandDark, borderRadius: 4, padding: 10, fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", color: th.text }}>{resposta}</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
